import { SignalDispatcher, SimpleEventDispatcher } from 'strongly-typed-events';
import { DomHelper } from '../common/dom-helper';
import { EditorConfig } from '../common/editor-config';
import { IntegrationInfo } from '../common/integration-info';
import { UrlHelper } from '../customers-canvas-product-se-entry';
import { PriceProvider } from './price-provider';

export class SimpleEditorWrapper {

    private _onReady = new SignalDispatcher();
    private _onFinish = new SimpleEventDispatcher();

    public constructor(
        private _integrationInfo: IntegrationInfo,
        private _editorConfig: EditorConfig,
        private _priceProvider?: PriceProvider,
        private _frame?: {
            doc: Document,
            win: any,
        },
        editorUrl?: string | -1,
    ) {
        this.disableAutoBootstrapIfNeeded();
        if (editorUrl !== -1) {
            console.log('[addScriptsAndStylesToPage] Ready');
            this.addScriptsAndStylesToPage(editorUrl);
        }
        this.initWithConfig();
        this.subscribeToEditorLoaded();
        this.subscribeToFinish();
    }

    get onReady() {
        return this._onReady;
    }

    get onFinish() {
        return this._onFinish;
    }

    get integrationInfo(): IntegrationInfo {
        return this._integrationInfo;
    };

    get element(): HTMLElement {
        return this.getFirstSimpleEditor();
    }

    public show() {
        const editorElement = this.getFirstSimpleEditor();
        // editorElement.hidden = false;

        editorElement.style.position = 'relative';
        editorElement.style.opacity = '1';
        editorElement.style.zIndex = '';
    }

    public hide() {
        const editorElement = this.getFirstSimpleEditor();
        // editorElement.hidden = true;

        editorElement.style.position = 'absolute';
        editorElement.style.opacity = '0';
        editorElement.style.zIndex = '-100';
    }

    private subscribeToEditorLoaded() {
        const _document = this._frame?.doc || document;
        _document.addEventListener('editorloaded', () => {
            this._onReady.dispatch();
        });
    }

    private subscribeToFinish() {
        const editorElement = this.getFirstSimpleEditor();
        editorElement.addEventListener('addtocart', e => {
            this._onFinish.dispatchAsync(e);
        });
    }

    private getFirstSimpleEditor() {
        const _document = this._frame?.doc || document;
        return _document.getElementsByTagName('au-simple-editor')[0] as HTMLElement;
    }

    private addScriptsAndStylesToPage(editorUrl?: string) {
        const frame = this._frame;
        const simpleEditorBaseUrl = (!!editorUrl) ? editorUrl : this._integrationInfo.simpleEditorUrl;
        DomHelper.addCss(UrlHelper.urlCombine(simpleEditorBaseUrl, "styles.css"), "simple_editor_style", frame);
        DomHelper.addScript(UrlHelper.urlCombine(simpleEditorBaseUrl, "editor.js"), "se-source", frame);
    }

    private initWithConfig() {
        let flag = false;
        const _document = this._frame?.doc || document;
        this.waitForCustomElement('au-simple-editor').then(() => {
            if (flag) return;
            flag = true;
            this.sourceScriptLoadHandler();
        }).catch(console.error);
        _document.getElementById('se-source')?.addEventListener('load', () => {
            if (flag) return;
            flag = true;
            this.sourceScriptLoadHandler();
        });
    }

    private async sourceScriptLoadHandler() {
        try {
            // await this.bootstrapIfNeeded();
            const editorElement = this.getFirstSimpleEditor() as any;
            await editorElement.init(this._editorConfig);
        } catch (error) {
            console.error(error);
        }
    }

    async bootstrapIfNeeded() {
    if (!this._priceProvider) return;

    const win = this._frame?.win || window;
    const editor = win.AurigmaWebComponents?.simpleEditor;
    if (!editor) return;

    if (typeof editor.bootstrap === 'function') {
        await editor.bootstrap([this._priceProvider]);
        return;
    }

    const priceProvider = this._priceProvider;
    let isFirst = true;

    try {
        Object.defineProperty(editor, 'bootstrap', {
            set(value) {
                if (typeof value === 'function' && isFirst) {
                    isFirst = false;
                    value([priceProvider]);
                }

                Object.defineProperty(editor, 'bootstrap', {
                    value,
                    writable: true,
                    enumerable: true,
                    configurable: true
                });
            },
            configurable: true,
            enumerable: true
        });
    } catch (err) {
        console.warn('Failed to intercept bootstrap:', err);
    }
}

    private async disableAutoBootstrapIfNeeded() {
        const win = this._frame?.win || window;
        if (!this._priceProvider) {
            return;
        }
        win.AurigmaWebComponents ??= {};
        win.AurigmaWebComponents.simpleEditor ??= {};
        win.AurigmaWebComponents.simpleEditor.autoBootstrapping = false; 
    }

    private waitForCustomElement(tagName: string, timeout = 8000) {
        if (customElements.get(tagName)) return Promise.resolve();

        return Promise.race([
            customElements.whenDefined(tagName),
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error(`⏱ Timeout: ${tagName}`)), timeout)
            )
        ]);
    }
}